import { CircularProgress, Link, Stack, Tooltip, Typography } from '@mui/material'
import { Reference, ReferenceType, TrendTopic, TwinInsightCategory, TwinInsightFull } from 'api/model'
import { useEffect, useState } from 'react'
import { COLORS } from 'app-constants'
import { ReactComponent as GoogleCalendar } from './icons/google-calendar.svg'
import { ReactComponent as GoogleDocument } from './icons/google-document.svg'
import { ReactComponent as GooglePresentation } from './icons/google-presentation.svg'
import { ReactComponent as GoogleSheet } from './icons/google-sheet.svg'
import { ReactComponent as GmailIcon } from './icons/gmail.svg'
import { ReactComponent as SlackIcon } from 'components/NavBar/slack.svg'
import { ReactComponent as PdfIcon } from './icons/pdf.svg'
import { ReactComponent as WebLinkIcon } from './icons/web-link.svg'
import { ReactComponent as NoteIcon } from './icons/note.svg'
import { ReactComponent as MicrosoftWordIcon } from './icons/microsoft-word.svg'
import { ReactComponent as MicrosoftPowerpointIcon } from './icons/microsoft-powerpoint.svg'
import { ReactComponent as MicrosoftExcelIcon } from './icons/microsoft-excel.svg'
import { ReactComponent as OtherIcon } from './icons/other.svg'
import { formatTimestamp } from 'utils'
import { myTwinAtom } from 'state/my-twin'
import { useRecoilValue } from 'recoil'
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress'
import { styled } from '@mui/material/styles'
import AutoGraph from '@mui/icons-material/AutoGraph'
import TopicLabel from './TopicLabel/TopicLabel'
import { trackButtonClick } from 'analytics/events'

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  width: '100%',
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#E9F0FE',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#3A7AFF',
  },
}))

const CardStack = styled(Stack)`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  align-items: flex-start;
  border: 1px solid #f4f4f4;
`

// TODO: move this out to utils
function getFormattedDateRange() {
  const today = new Date()
  const oneWeekAgo = new Date()
  oneWeekAgo.setDate(today.getDate() - 7)

  // Create a formatter for "MMM DD" format
  const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
  })

  // Create a formatter for "MMM DD, YYYY" format
  const fullDateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  // Format the dates
  const oneWeekAgoFormatted = monthDayFormatter.format(oneWeekAgo)
  const todayFormatted = fullDateFormatter.format(today)

  // Return the formatted date range
  return `${oneWeekAgoFormatted} - ${todayFormatted}`
}

const TrendBadge = ({ trendTopic, trendId }: { trendTopic?: TrendTopic; trendId?: string }) => {
  return (
    <Link
      onClick={() => {
        trackButtonClick('clicked_view_trend')
      }}
      href={`/trends/company?name=${trendTopic?.company.system_name}&topicType=${trendTopic?.type}&trendId=${trendId}`}
      sx={{
        display: 'flex',
        border: '1px solid rgb(221, 221, 221)',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        color: COLORS.PURE_BLACK,
        padding: '8px 26px',
        alignItems: 'center',
        '&:hover': {
          background: 'rgb(248, 248, 248)',
          border: '1px solid rgb(221, 221, 221)',
        },
      }}>
      <AutoGraph
        sx={{
          width: '16px',
          height: '16px',
          marginRight: '4px',
        }}
      />
      View trend
    </Link>
  )
}

export const ReferenceItem = ({
  reference,
  referenceIdMap,
  isReferenceIdVisible,
}: {
  reference: Reference
  referenceIdMap: Map<String, number>
  isReferenceIdVisible?: boolean // only visible if observations are present
}) => {
  const getIcon = () => {
    let icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>

    switch (reference.type) {
      case ReferenceType.google_calendar:
        icon = GoogleCalendar
        break
      case ReferenceType.google_document:
        icon = GoogleDocument
        break
      case ReferenceType.google_presentation:
        icon = GooglePresentation
        break
      case ReferenceType.google_sheet:
        icon = GoogleSheet
        break
      case ReferenceType.gmail:
        icon = GmailIcon
        break
      case ReferenceType.pdf:
        icon = PdfIcon
        break
      case ReferenceType.web_link:
        icon = WebLinkIcon
        break
      case ReferenceType.note:
        icon = NoteIcon
        break
      case ReferenceType.microsoft_word:
        icon = MicrosoftWordIcon
        break
      case ReferenceType.microsoft_powerpoint:
        icon = MicrosoftPowerpointIcon
        break
      case ReferenceType.microsoft_excel:
        icon = MicrosoftExcelIcon
        break
      case ReferenceType.slack:
        icon = SlackIcon
        break
      default:
        icon = OtherIcon
        break
    }
    return icon
  }

  let Icon = getIcon()

  return (
    <Stack
      direction="row"
      p="6px"
      borderRadius="8px"
      alignItems="center"
      maxWidth="193px"
      height="40px"
      width="fit-content"
      onClick={() => window.open(reference.url, '_blank')}
      sx={{
        cursor: 'pointer',
        background: COLORS.GRAY_7,
      }}>
      {reference.type && (
        <Stack flex="0 0 20px" width="20px">
          <Icon />
        </Stack>
      )}

      <Tooltip title={reference.title} enterDelay={2}>
        <Typography
          width="100%"
          mx="10px"
          variant="h6"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {reference.title}
        </Typography>
      </Tooltip>

      {referenceIdMap.get(reference.document_id || reference.title) && isReferenceIdVisible && (
        <Typography variant="h8.5" color={COLORS.GRAY_1} p="4px">
          {referenceIdMap.get(reference.document_id || reference.title)}
        </Typography>
      )}
    </Stack>
  )
}

const ReferencesList = ({
  referenceIdMap,
  references,
  isReferenceIdVisible,
}: {
  referenceIdMap: Map<String, number>
  references: Reference[]
  isReferenceIdVisible: boolean
}) => {
  const [isReferencesExpanded, setIsReferencesExpanded] = useState(false)

  const firstThreeReferences = references.slice(0, 3)
  const additionalReferencesCount = references.length - firstThreeReferences.length

  const visibleReferences = isReferencesExpanded ? references : firstThreeReferences

  return (
    <Stack direction="row" gap="8px" width="100%" flexWrap="wrap">
      {visibleReferences.map((r, index) => (
        <ReferenceItem
          key={index}
          reference={r}
          referenceIdMap={referenceIdMap}
          isReferenceIdVisible={isReferenceIdVisible}
        />
      ))}

      {additionalReferencesCount > 0 && !isReferencesExpanded && (
        <Stack
          direction="row"
          alignItems="center"
          p="6px 12px"
          borderRadius="8px"
          onClick={() => setIsReferencesExpanded(!isReferencesExpanded)}
          sx={{
            cursor: 'pointer',
            background: COLORS.GRAY_7,
          }}>
          <Typography variant="h6" color={COLORS.GRAY_1}>
            View {additionalReferencesCount} more
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

const ReferenceBadges = ({
  referenceIdMap,
  references,
}: {
  referenceIdMap: Map<String, number>
  references: Reference[]
}) => {
  // need to create an array of new reference ids indexing from 1
  const referenceIds = references.map((ref) => referenceIdMap.get(ref.document_id || ref.title))

  return (
    <Stack direction="row" spacing="4px" ml="4px" mt="4px" position="absolute" display="inline-flex">
      {referenceIds.map((id, index) => (
        <Stack
          key={index}
          borderRadius="20px"
          justifyContent="center"
          alignItems="center"
          onClick={() => {
            const reference = references.find((ref) => referenceIdMap.get(ref.document_id || ref.title) === id)
            if (reference) {
              window.open(reference.url, '_blank')
            }
          }}
          sx={{
            cursor: 'pointer',
            backgroundColor: COLORS.GRAY_7,
            height: '16px',
            width: '16px',
            '&:hover': {
              backgroundColor: COLORS.GRAY_6,
            },
          }}>
          <Typography variant="h8.5" color={COLORS.GRAY_1}>
            {id}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export function getReferenceIdMap(references: Reference[]): Map<String, number> {
  const referenceIdMap = new Map()
  references.forEach((ref, index) => {
    referenceIdMap.set(ref.document_id || ref.title, index + 1)
  })
  return referenceIdMap
}

const InsightCard = ({ insight }: { insight: TwinInsightFull }) => {
  const referenceIdMap = getReferenceIdMap(insight.references)
  return (
    <CardStack
      id={insight.trend_topic ? insight.trend_topic.id : insight.category.name}
      sx={{
        scrollMarginTop: '112px', // needed to account for the fixed header, so the scroll doesn't hide the card under the header
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb="20px" width="100%">
        {insight.trend_topic && insight.trend_id ? (
          <Typography variant="h5">{insight.category.name}</Typography>
        ) : (
          <TopicLabel text={insight.category.name} color={insight.category.color} />
        )}

        {insight.trend_topic && insight.trend_id && (
          <TrendBadge trendTopic={insight.trend_topic} trendId={insight.trend_id} />
        )}
      </Stack>

      {insight.summary && (
        <Typography variant="pS" mb={3}>
          <Typography
            variant="pSB"
            sx={{
              whiteSpace: 'pre-line',
            }}>
            Summary:{' '}
          </Typography>
          {insight.summary}
        </Typography>
      )}

      {insight.observations.length > 0 && (
        <ul
          style={{
            marginLeft: '-12px',
            marginBottom: '24px',
          }}>
          {insight.observations.map((obs, index) => (
            <li
              key={index}
              style={{
                marginBottom: index !== insight.observations.length - 1 ? '8px' : '0',
              }}>
              <Typography
                variant="pS"
                key={index}
                sx={{
                  position: 'relative',
                }}>
                {obs.content}
                <ReferenceBadges referenceIdMap={referenceIdMap} references={obs.references} />
              </Typography>
            </li>
          ))}
        </ul>
      )}

      <ReferencesList
        referenceIdMap={referenceIdMap}
        references={insight.references}
        isReferenceIdVisible={insight.observations.length > 0}
      />
    </CardStack>
  )
}

export const TimeBadge = ({ date }: { date: string }) => {
  return (
    <Stack
      p="4px 10px"
      borderRadius="20px"
      sx={{
        width: 'fit-content',
        background: COLORS.JELLED_MID_BLUE_10_PERCENT,
      }}>
      <Typography
        variant="pXS"
        sx={{
          color: COLORS.JELLED_PRIMARY_BLUE,
        }}>
        {date}
      </Typography>
    </Stack>
  )
}

// define a helper function that removes all \n from the beginning of a string
function removeLeadingNewlines(str: string) {
  return str.replace(/^\n+/, '')
}

const TodayInsights = ({
  latest,
  isLoadingTwinListInsights,
}: {
  isLoadingTwinListInsights: boolean
  latest: TwinInsightFull[]
}) => {
  // sort latest insights by category name
  const sortedLatest = latest.sort((a, b) => a.category.name.localeCompare(b.category.name))
  // move the "Other" category to the end
  const otherIndex = sortedLatest.findIndex((l) => l.category.name === 'Other')
  if (otherIndex !== -1) {
    const other = sortedLatest.splice(otherIndex, 1)
    sortedLatest.push(other[0])
  }

  return (
    <Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Today's insights</Typography>
        <TimeBadge date={formatTimestamp(new Date().toLocaleDateString())} />
      </Stack>

      {isLoadingTwinListInsights && (
        <Stack width="100%" height="180px" justifyContent="center" alignItems="center">
          <CircularProgress size={24} sx={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
        </Stack>
      )}

      {latest.length > 0 ? (
        <CardStack spacing={4}>
          {sortedLatest.map((l, index) => (
            <Stack key={index}>
              <TopicLabel text={l.category.name} color={l.category.color} />
              <ul
                style={{
                  marginBottom: 0,
                }}>
                {l.observations.map((observation, index) => (
                  <li key={index}>
                    <Typography
                      variant="pS"
                      sx={{
                        whiteSpace: 'pre-line',
                      }}>
                      {removeLeadingNewlines(observation.content)}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Stack>
          ))}
        </CardStack>
      ) : (
        <Stack>
          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            No insights available yet
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

const EarlierInsights = ({
  insights,
  isLoadingTwinListInsights,
}: {
  isLoadingTwinListInsights: boolean
  insights: TwinInsightFull[]
}) => {
  return (
    <Stack mt={6} mb={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Earlier insights</Typography>
        <TimeBadge date={getFormattedDateRange()} />
      </Stack>

      {isLoadingTwinListInsights && (
        <Stack width="100%" height="180px" justifyContent="center" alignItems="center">
          <CircularProgress size={24} sx={{ color: COLORS.GRAY_0 }} />
        </Stack>
      )}

      {insights.length > 0 ? (
        <Stack spacing={3}>
          {insights.map((insight, index) => (
            <InsightCard key={index} insight={insight} />
          ))}
        </Stack>
      ) : (
        <Stack>
          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            No insights available yet
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

const InsightsList = ({
  selectedCategory,
  selectedTrend,
  insights,
  latest,
  isLoadingTwinListInsights,
}: {
  selectedCategory: TwinInsightCategory | null
  selectedTrend: TrendTopic | null
  insights: TwinInsightFull[]
  latest: TwinInsightFull[]
  isLoadingTwinListInsights: boolean
}) => {
  const myTwin = useRecoilValue(myTwinAtom)

  // whenever selected insight changes, we want to scroll it to the top of the page
  useEffect(() => {
    const element = document.getElementById(selectedCategory?.name ?? '')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedCategory?.name])

  // whenever selected trend changes, we want to scroll it to the top of the page
  useEffect(() => {
    const element = document.getElementById(selectedTrend?.id ?? '')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedTrend?.id])

  // if the insights are still in the process of generating, we should be showing a different UI
  const cachedPercentageCompletion = localStorage.getItem('insights_onboarding_percent')
    ? Number(localStorage.getItem('insights_onboarding_percent'))
    : 0
  const percentageCompletion = myTwin?.insights_onboarding_percent || cachedPercentageCompletion
  const [displayedPercentage, setDisplayedPercentage] = useState<number>(percentageCompletion)

  // slowly increase the displayed percentage to give the user a sense of progress
  useEffect(() => {
    if (percentageCompletion === 0) {
      const interval = setInterval(() => {
        setDisplayedPercentage((prev) => {
          if (prev < 14) {
            localStorage.setItem('insights_onboarding_percent', `${prev + 1}`)
            return prev + 1
          } else {
            localStorage.setItem('insights_onboarding_percent', `${prev}`)
            return prev
          }
        })
        if (displayedPercentage >= 14) {
          clearInterval(interval)
        }
      }, 5000)
    } else {
      setDisplayedPercentage(percentageCompletion)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentageCompletion])

  if (!myTwin) {
    return null
  }

  if (displayedPercentage < 100) {
    return (
      <Stack
        p={5}
        borderRadius="8px"
        alignItems="flex-start"
        gap="24px"
        flex="1 0 0"
        sx={{
          width: '100%',
          background: '#FFF',
          boxShadow: '2px 2px 20px 0px rgba(0, 0, 0, 0.05)',
        }}>
        <Typography variant="h5">Indexing emails and generating insights...</Typography>

        <Typography
          variant="pS"
          sx={{
            color: COLORS.GRAY_0,
          }}>
          {displayedPercentage}% completed
        </Typography>

        <BorderLinearProgress variant="determinate" value={displayedPercentage} />

        {insights.length > 0 && (
          <Stack spacing={3}>
            {insights.map((insight, index) => {
              return <InsightCard key={index} insight={insight} />
            })}
          </Stack>
        )}
      </Stack>
    )
  }

  return (
    <Stack width="100%">
      <TodayInsights latest={latest} isLoadingTwinListInsights={isLoadingTwinListInsights} />
      <EarlierInsights insights={insights} isLoadingTwinListInsights={isLoadingTwinListInsights} />
    </Stack>
  )
}

export default InsightsList
